import React from 'react';

interface SkeletonProps {
    className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

export const UserManagementSkeleton: React.FC = () => (
    <div className="">
        {/* <div className="mb-4 sm:mb-6">
            <Skeleton className="h-8 w-48" />
        </div> */}

       
        <div className="bg-white rounded-card shadow overflow-hidden p-3 sm:p-4">
            <div className="min-h-[500px]">
                <div className="space-y-4">
                    {/* Table header */}
                    <div className="grid grid-cols-4 gap-4 border-b pb-4">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={`header-${i}`} className="h-6" />
                        ))}
                    </div>
                    
                    {/* Table rows */}
                    {[...Array(5)].map((_, rowIndex) => (
                        <div key={`row-${rowIndex}`} className="grid grid-cols-4 gap-4 py-4 border-b">
                            {[...Array(4)].map((_, colIndex) => (
                                <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-6" />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-6">
                    <Skeleton className="h-8 w-32" />
                    <div className="flex gap-2">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={`page-${i}`} className="h-8 w-8" />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const JobsiteManagementSkeleton: React.FC = () => (
    <div className="">
        {/* Table view skeleton */}
        <div className="bg-white rounded-[12px] shadow">
            <div className="overflow-x-auto p-3 sm:p-4">
                <div className="min-w-full">
                    {/* Table header skeleton */}
                    <div className="grid grid-cols-6 gap-4 border-b pb-4">
                        <Skeleton className="h-6 w-32" /> {/* Jobsite */}
                        <Skeleton className="h-6 w-48" /> {/* Address */}
                        <Skeleton className="h-6 w-32" /> {/* Contractor */}
                        <Skeleton className="h-6 w-24" /> {/* Status */}
                        <Skeleton className="h-6 w-24" /> {/* Completion */}
                        <Skeleton className="h-6 w-32" /> {/* Submitted by */}
                    </div>

                    {/* Table rows skeleton */}
                    {[...Array(8)].map((_, rowIndex) => (
                        <div key={`row-${rowIndex}`} className="grid grid-cols-6 gap-4 py-4 border-b">
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-6 w-48" />
                            <Skeleton className="h-6 w-32" />
                            <div className="flex">
                                <Skeleton className="h-6 w-24 rounded-full" />
                            </div>
                            <Skeleton className="h-6 w-16" />
                            <Skeleton className="h-6 w-32" />
                        </div>
                    ))}

                    {/* Pagination skeleton */}
                    <div className="flex justify-between items-center mt-6">
                        <Skeleton className="h-8 w-32" />
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={`page-${i}`} className="h-8 w-8" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const JobsiteDetailsSkeleton: React.FC = () => (
    <div className="">
        {/* Navigation skeleton */}
        {/* <div className="mb-6 flex items-center">
            <Skeleton className="w-5 h-8 mr-2" />
            <Skeleton className="h-8 w-48" />
        </div> */}



        {/* Header section skeleton */}
        <div className="bg-white rounded-md shadow mb-4">
            <div className="p-4 sm:p-6">
                {/* Title and buttons */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-4 sm:gap-0">
                    <div>
                        <Skeleton className="h-8 w-64 mb-2" />
                        <Skeleton className="h-5 w-48" />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Skeleton className="h-10 w-32" />
                        <Skeleton className="h-10 w-32" />
                        <Skeleton className="h-10 w-32" />
                    </div>
                </div>

                {/* Address and status section */}
                <div className="space-y-4">
                    <div className="flex items-center">
                        <Skeleton className="h-6 w-96" />
                        <Skeleton className="h-6 w-24 ml-2" />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between gap-6 sm:gap-0">
                        <div className="w-full sm:w-[30%]">
                            {/* Job completion skeleton */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-2">
                                    <Skeleton className="h-6 w-32" />
                                    <Skeleton className="h-6 w-16" />
                                </div>
                                <Skeleton className="h-2 w-full rounded-full" />
                            </div>

                            {/* Status toggle skeleton */}
                            <div className="flex items-center gap-2">
                                <Skeleton className="h-6 w-16" />
                                <Skeleton className="h-6 w-11 rounded-full" />
                                <Skeleton className="h-6 w-16" />
                            </div>
                        </div>

                        {/* Submitted info skeleton */}
                        <div className="w-full sm:w-[30%] text-left sm:text-right">
                            <div className="mb-3">
                                <Skeleton className="h-6 w-48 mb-2 ml-auto" />
                                <Skeleton className="h-6 w-64 ml-auto" />
                            </div>
                            <Skeleton className="h-6 w-24 ml-auto" />
                        </div>
                    </div>
                </div>

                {/* Latest action skeleton */}
                <div className="bg-gray-100 p-3 sm:p-4 rounded-md mt-4">
                    <Skeleton className="h-6 w-32 mb-2" />
                    <Skeleton className="h-4 w-full" />
                </div>
            </div>
        </div>

        {/* Workers and Trades section skeleton */}
        <div className="bg-white rounded-md shadow mb-4">
            <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                    {/* Workers section */}
                    <div>
                        <Skeleton className="h-7 w-32 mb-4" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Skeleton className="h-6 w-24 mb-2" />
                                <Skeleton className="h-6 w-32" />
                            </div>
                            <div>
                                <Skeleton className="h-6 w-24 mb-2" />
                                <Skeleton className="h-6 w-32" />
                            </div>
                        </div>
                    </div>
                    {/* Trades section */}
                    <div>
                        <Skeleton className="h-7 w-32 mb-4" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Skeleton className="h-6 w-48 mb-2" />
                                <Skeleton className="h-6 w-32" />
                            </div>
                            <div>
                                <Skeleton className="h-6 w-24 mb-2" />
                                <Skeleton className="h-6 w-32" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Job Details section skeleton */}
        <div className="bg-white rounded-md shadow mb-4">
            <div className="p-4 sm:p-6">
                <Skeleton className="h-7 w-32 mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className="mb-3">
                            <Skeleton className="h-6 w-32 mb-2" />
                            <Skeleton className="h-6 w-48" />
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Comments section skeleton */}
        <div className="bg-white rounded-md shadow mb-4">
            <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-5">
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-10 w-48" />
                </div>
                <div className="space-y-4">
                    {[...Array(3)].map((_, index) => (
                        <div key={index} className="border-b border-gray-300 pb-4">
                            <Skeleton className="h-5 w-full mb-2" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

export const StatCardSkeleton: React.FC = () => (
    <div className="bg-white rounded-md shadow-lg p-4 mb-4">
        <div className="flex justify-between items-start mb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-6 rounded-full" />
        </div>
        <div className="space-y-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-48" />
        </div>
    </div>
);

export const JobsiteCardSkeleton: React.FC = () => (
    <div className="bg-white p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-3">
            <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
            </div>
        </div>
        <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <div className="flex justify-between items-center">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
        </div>
    </div>
);

export const ChartSkeleton: React.FC<{ height?: string }> = ({ height = "h-[300px]" }) => (
    <div className={`bg-white rounded-md border p-4 ${height}`}>
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="flex flex-col space-y-4">
            <Skeleton className="h-full w-full" />
        </div>
    </div>
);

export const CompletionStagesSkeleton: React.FC = () => (
    <div className="bg-white rounded-md border p-4">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="grid grid-cols-7 gap-2">
                    <Skeleton className="h-8 col-span-1" />
                    {[...Array(6)].map((_, j) => (
                        <Skeleton key={j} className="h-8" />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

export const WorkersPercentageSkeleton: React.FC = () => (
    <div className="bg-white rounded-md border p-4 h-full">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="space-y-4">
            {[...Array(13)].map((_, i) => (
                <div key={i} className="flex flex-col space-y-2">
                    <div className="flex justify-between">
                        <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                </div>
            ))}
        </div>
    </div>
);

export const FundingSourcesSkeleton: React.FC = () => (
    <div className="bg-white rounded-md border p-4">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="flex justify-center items-center h-[300px]">
            <div className="relative w-48 h-48">
                <Skeleton className="w-full h-full rounded-full" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white rounded-full" />
            </div>
        </div>
    </div>
);

export const ComparisonChartSkeleton: React.FC = () => (
    <div className="bg-white rounded-md border p-4">
        <Skeleton className="h-6 w-64 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
                <div key={i} className="space-y-2">
                    <Skeleton className="h-5 w-32 mx-auto" />
                    <div className="h-[300px]">
                        <Skeleton className="h-full w-full" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export const ReportDetailsSkeleton: React.FC = () => (
    <div className="">
        {/* Header/Breadcrumb skeleton */}
        {/* When filters are not yet applied */}
        <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
            <div className="flex items-center justify-between">
                <div className="relative flex-grow sm:flex-grow-0">
                    <Skeleton className="w-full sm:w-64 h-10 rounded-[8px]" /> {/* Search input */}
                </div>
                <div className="flex gap-2">
                    <Skeleton className="w-24 h-10 rounded-md" /> {/* Filters button */}
                    <Skeleton className="w-24 h-10 rounded-md" /> {/* Export button */}
                </div>
            </div>
        </div>

        {/* Table container */}
        <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
            <div className="overflow-x-auto">
                <div className="min-h-[70vh] flex flex-col">
                    {/* Table header skeleton */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 border-b pb-4">
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={`header-${i}`} className="h-6 w-full" />
                        ))}
                    </div>
                    
                    {/* Table rows */}
                    <div className="flex-grow">
                        {[...Array(8)].map((_, rowIndex) => (
                            <div key={`row-${rowIndex}`} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 py-4 border-b">
                                {[...Array(6)].map((_, colIndex) => (
                                    <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-6 w-full" />
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-6">
                        <Skeleton className="h-6 w-32" />
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={`page-${i}`} className="h-8 w-8 rounded-md" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const JobsiteLogsSkeleton: React.FC = () => (
    <div className="">
        {/* <div className="mb-4 sm:mb-6">
        <div className="mb-6 flex items-center">
            <Skeleton className="w-5 h-8 mr-2" />
            <Skeleton className="h-8 w-48" />
        </div>
        </div> */}

        {/* <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0">
                        <Skeleton className="h-10 w-64" />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
                    <Skeleton className="h-10 w-32" />
                </div>
            </div>
        </div> */}

        <div className="bg-white rounded-[12px] shadow p-3 sm:p-4">
            <div className="relative min-h-[300px]">
                <div className="space-y-4">
                    {/* Table header */}
                    <div className="grid grid-cols-5 gap-4 border-b pb-4">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={`header-${i}`} className="h-6" />
                        ))}
                    </div>
                    
                    {/* Table rows */}
                    {[...Array(5)].map((_, rowIndex) => (
                        <div key={`row-${rowIndex}`} className="grid grid-cols-5 gap-4 py-4 border-b">
                            {[...Array(5)].map((_, colIndex) => (
                                <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-6" />
                            ))}
                        </div>
                    ))}

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-6">
                        <Skeleton className="h-8 w-32" />
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <Skeleton key={`page-${i}`} className="h-8 w-8" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const ReportsPageSkeleton: React.FC = () => (
    <div className="bg-gray-50">
        <div className="">
            {/* Removed breadcrumb skeleton */}
            <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4 min-h-[70vh]">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {[...Array(6)].map((_, index) => (
                        <div
                            key={index}
                            className="bg-white flex items-center p-5 border border-border rounded-card text-left w-full"
                        >
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4">
                                <Skeleton className="w-6 h-6 rounded" />
                            </div>
                            <div className="flex-1">
                                <Skeleton className="h-6 w-3/4 mb-2" />
                                <Skeleton className="h-4 w-full" />
                            </div>
                            <div className="ml-4">
                                <Skeleton className="w-5 h-5" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

export const ContractorStatisticsSkeleton: React.FC = () => (
    <div className="bg-white rounded-md border p-4">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-12 w-24 mb-4" />
        <div className="space-y-3">
            <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-8" />
            </div>
            <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-8" />
            </div>
            <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-8" />
            </div>
        </div>
    </div>
); 